import type {
  SelectedElementDesignationElementTransformed,
  UniversalModifierValue,
} from "schemas/generated/fileModifiers";

import { exhaustiveSwitch } from "replo-utils/lib/misc";

// Consistent formatting class mapping
export const CONTROLLED_FORMATTING_CLASSES = {
  "font-bold": "bold",
  italic: "italic",
  underline: "underline",
  "line-through": "strikethrough",
} as const;

export type FormattingState = {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikethrough: boolean;
};

/**
 *  * Note (Nick, 2025-10-31): All possible StyleOverrides keys
 */
type StyleOverridesElementTransformed =
  SelectedElementDesignationElementTransformed["element"]["styleOverrides"];

/**
 * Parse existing text formatting from styleOverrides and className
 * Uses computed styles where available, falls back to className parsing
 * @param styleOverrides - Computed styles from the element (all properties, but only text formatting ones are used)
 * @param className - CSS class names from the element
 * @returns Object with boolean values for each formatting state
 */
export function parseTextFormatting(
  styleOverrides: StyleOverridesElementTransformed = {},
  className: string = "",
): FormattingState {
  const classes = new Set(
    className.split(" ").filter((cls: string) => cls.trim() !== ""),
  );
  const activeFormatting: Record<string, boolean> = {};

  // Use styleOverrides for properties that are available there
  // Bold detection from fontWeight (700+ is considered bold)
  activeFormatting.bold = (styleOverrides?.fontWeight ?? 400) >= 700;

  // Italic detection from fontStyle
  activeFormatting.italic = styleOverrides?.fontStyle === "italic";

  // Text decoration detection from computed styles
  const textDecorationLine = styleOverrides?.textDecorationLine;
  if (textDecorationLine) {
    const decorationLines = new Set(
      textDecorationLine.split(" ").map((line: string) => line.trim()),
    );
    activeFormatting.underline = decorationLines.has("underline");
    activeFormatting.strikethrough = decorationLines.has("line-through");
  }

  // Fall back to className parsing for any properties not detected from styleOverrides
  Object.keys(CONTROLLED_FORMATTING_CLASSES).forEach((cssClass) => {
    const formatKey =
      CONTROLLED_FORMATTING_CLASSES[
        cssClass as keyof typeof CONTROLLED_FORMATTING_CLASSES
      ];

    // Only set from className if not already set from styleOverrides
    if (!(formatKey in activeFormatting)) {
      activeFormatting[formatKey] = classes.has(cssClass);
    }
  });

  // Override with className if present (className takes precedence)
  if (classes.has("font-bold")) {
    activeFormatting.bold = true;
  }
  if (classes.has("italic")) {
    activeFormatting.italic = true;
  }
  if (classes.has("underline")) {
    activeFormatting.underline = true;
  }
  if (classes.has("line-through")) {
    activeFormatting.strikethrough = true;
  }

  return {
    bold: activeFormatting.bold ?? false,
    italic: activeFormatting.italic ?? false,
    underline: activeFormatting.underline ?? false,
    strikethrough: activeFormatting.strikethrough ?? false,
  };
}

/**
 * Build className string from formatting state
 * @param formatting - The formatting state object
 * @returns Space-separated CSS class names
 */
export function buildFormattingClassName(formatting: FormattingState): string {
  const formattingClasses: string[] = [];

  if (formatting.bold) {
    formattingClasses.push("font-bold");
  }
  if (formatting.italic) {
    formattingClasses.push("italic");
  }
  if (formatting.underline) {
    formattingClasses.push("underline");
  }
  if (formatting.strikethrough) {
    formattingClasses.push("line-through");
  }

  return formattingClasses.join(" ");
}

/**
 * Generate a unique key for a modifier based on its type and discriminator fields.
 * This key is used to deduplicate actions in the modifier queue.
 *
 * The key includes all fields that identify WHAT is being modified, but excludes
 * the actual value being changed. This ensures that:
 * - padding-top and padding-left are treated as different modifiers
 * - Multiple changes to padding-top only keep the latest value
 *
 * Examples:
 * - { type: "padding", direction: "top", value: 10 } → '{"type":"padding","direction":"top"}'
 * - { type: "padding", direction: "left", value: 20 } → '{"type":"padding","direction":"left"}'
 * - { type: "dimension", side: "width", value: 100 } → '{"type":"dimension","side":"width"}'
 * - { type: "cornerRadius", corner: "topLeft", value: 8 } → '{"type":"cornerRadius","corner":"topLeft"}'
 * - { type: "color", value: "#fff" } → '{"type":"color"}'
 */
export function getModifierKey(modifier: UniversalModifierValue): string {
  const { value, ...rest } = modifier as any;
  return JSON.stringify(rest);
}

export function getCssPropertiesFromModifier(
  modifier: UniversalModifierValue,
): Record<string, string | null> | null {
  return exhaustiveSwitch(
    modifier,
    "type",
  )({
    content: (): Record<string, string> | null => null,

    alignment: (m) => ({
      "text-align": m.value,
    }),

    fontSize: (m) => ({
      "font-size": m.value === null ? null : `${m.value}px`,
    }),

    fontWeight: (m) => ({
      "font-weight": m.value === null ? null : String(m.value),
    }),

    lineHeight: (m) => ({
      "line-height": m.value === null ? null : String(m.value),
    }),

    textSpacing: (m) => ({
      "letter-spacing": m.value === null ? null : `${m.value}em`,
    }),

    fontFamily: (m) => ({
      "font-family": m.value,
    }),

    textShadow: (m) => ({
      "text-shadow": `${m.value.offsetX}px ${m.value.offsetY}px ${
        m.value.blurRadius ?? 0
      }px ${m.value.color ?? "rgba(0,0,0,0.5)"}`,
    }),

    padding: (m): Record<string, string | null> | null => {
      const value = m.value === null ? null : `${m.value}px`;
      switch (m.direction) {
        case "top":
          return { "padding-top": value };
        case "right":
          return { "padding-right": value };
        case "bottom":
          return { "padding-bottom": value };
        case "left":
          return { "padding-left": value };
        default:
          return null;
      }
    },

    margin: (m): Record<string, string | null> | null => {
      // NOTE (Kevin, 2025-12-10): Handle "auto" value for margin centering
      const getMarginValue = () => {
        if (m.value === null) {
          return null;
        }
        if (m.value === "auto") {
          return "auto";
        }
        return `${m.value}px`;
      };
      const value = getMarginValue();
      switch (m.direction) {
        case "top":
          return { "margin-top": value };
        case "right":
          return { "margin-right": value };
        case "bottom":
          return { "margin-bottom": value };
        case "left":
          return { "margin-left": value };
        default:
          return null;
      }
    },

    dimension: (m): Record<string, string> => {
      let dimensionValue: string;
      if (m.value === "auto") {
        dimensionValue = "auto";
      } else if (m.value === "full") {
        dimensionValue = "100%";
      } else {
        dimensionValue = `${m.value}px`;
      }

      if (m.side === "width") {
        return { width: dimensionValue };
      } else {
        return { height: dimensionValue };
      }
    },

    boxShadow: (m) => {
      const shadowMap = {
        none: "none",
        "2xs": "0 1px 2px 0 rgb(0 0 0 / 0.05)",
        xs: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
        sm: "0 2px 4px -2px rgb(0 0 0 / 0.1), 0 4px 6px -1px rgb(0 0 0 / 0.1)",
        md: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
        lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
        xl: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
        "2xl": "0 25px 50px -12px rgb(0 0 0 / 0.25)",
      };
      return { "box-shadow": shadowMap[m.value] };
    },

    containerDirection: (m) => ({
      "flex-direction": m.value,
    }),

    containerAlignment: (m) => {
      const alignmentMap = {
        start: "flex-start",
        center: "center",
        end: "flex-end",
      };
      return { "justify-content": alignmentMap[m.value] };
    },

    containerGap: (m) => ({
      gap: `${m.value}px`,
    }),

    opacity: (m) => ({
      opacity: String(m.value),
    }),

    color: (m) => ({
      color: m.value ?? "inherit",
    }),

    backgroundColor: (m) => ({
      "background-color": m.value ?? "transparent",
    }),

    borderColor: (m) => ({
      "border-color": m.value ?? "transparent",
    }),

    borderStyle: (m) => ({
      "border-top-style": m.value,
      "border-right-style": m.value,
      "border-bottom-style": m.value,
      "border-left-style": m.value,
    }),

    borderWidth: (m): Record<string, string> | null => {
      switch (m.direction) {
        case "top":
          return { "border-top-width": `${m.value}px` };
        case "right":
          return { "border-right-width": `${m.value}px` };
        case "bottom":
          return { "border-bottom-width": `${m.value}px` };
        case "left":
          return { "border-left-width": `${m.value}px` };
        default:
          return null;
      }
    },

    cornerRadius: (m): Record<string, string> | null => {
      const radiusValue = m.value === "full" ? "9999px" : `${m.value}px`;
      switch (m.corner) {
        case "topLeft":
          return { "border-top-left-radius": radiusValue };
        case "topRight":
          return { "border-top-right-radius": radiusValue };
        case "bottomRight":
          return { "border-bottom-right-radius": radiusValue };
        case "bottomLeft":
          return { "border-bottom-left-radius": radiusValue };
        default:
          return null;
      }
    },

    textTag: () => null,
    asset: () => null,
    objectFit: (m) => ({
      "object-fit": m.value,
    }),
    aspectRatio: (m) => ({
      "aspect-ratio": m.value,
    }),
    product: () => null,
    variant: () => null,
    delete: () => null,
    href: () => null,
  });
}
